<template>
  <v-app :class="isDarkTheme ? 'v-theme--dark' : 'v-theme--light'" id="app">
    <nav-bar v-if="routeBaseName !== 'Maintenance'"></nav-bar>
    <v-main :class="`main-${$route.name?.toLowerCase()}`">
      <router-view @baseYearChanged="updateBodyClass"></router-view>
    </v-main>
    <Footer v-if="routeBaseName !== 'Maintenance'"></Footer>

    <!-- Localhost fallback launcher (used only if embedded widget fails) -->
    <SupportChatLauncher
      v-if="!hideFloatingSupportChrome && supportChatProvider === 'lhc'"
    />

    <LifeOnLifeSupportLauncher
      v-if="
        !hideFloatingSupportChrome &&
        supportChatProvider === 'lifeOnLifeSupport'
      "
    />

    <!-- Feedback Tab and Drawer (visible to tester, admin, superAdmin, owner) -->
    <template v-if="showFeedback && !hideFloatingSupportChrome">
      <FeedbackTab
        v-if="!notesDrawerOpen"
        :disabled="notesDrawerOpen"
        @toggle="toggleFeedbackDrawer"
      />
      <FeedbackDrawer v-model="feedbackDrawerOpen" />
    </template>

    <!-- Non-production builds only: shows which Firebase project this site uses -->
    <EnvironmentBadge />
  </v-app>
</template>

<script>
  import NavBar from '@/components/utilities/NavBar'
  import Footer from '@/components/utilities/Footer'
  import FeedbackTab from '@/components/feedback/FeedbackTab'
  import FeedbackDrawer from '@/components/feedback/FeedbackDrawer'
  import SupportChatLauncher from '@/components/utilities/SupportChatLauncher.vue'
  import LifeOnLifeSupportLauncher from '@/components/utilities/LifeOnLifeSupportLauncher.vue'
  import EnvironmentBadge from '@/components/utilities/EnvironmentBadge.vue'
  import firebase from 'firebase'
  import db from '@/firebase/init'
  import {
    initLiveHelperChat,
    hideLiveHelperChat,
    showLiveHelperChat,
    startLiveHelperChatSuppression,
    stopLiveHelperChatSuppression,
  } from '@/services/LiveHelperChatService'
  import { initZendesk, showZendesk, hideZendesk } from '@/services/ZendeskService'
  import { currentTheme } from '@/plugins/vuetify'
  import emitter from '@/eventBus'

  export default {
    name: 'App',
    computed: {
      isDarkTheme() {
        return currentTheme.value === 'dark'
      },
      routeBaseName() {
        return (
          (this.$route.meta && this.$route.meta.baseName) ||
          this.$route.name ||
          ''
        )
      },
      isPdfRoute() {
        return ['Pdf', 'SectionPdf', 'UnitPdf', 'CoursePdf'].includes(
          this.routeBaseName
        )
      },
      hideFloatingSupportChrome() {
        // Print/PDF views should not include feedback or help-desk widgets.
        return this.routeBaseName === 'Maintenance' || this.isPdfRoute
      },
      showFeedback() {
        // Check if feedback feature is enabled OR user is a tester (like notifications)
        const getFeatures = this.$store.getters['AppData/getFeatures'] || []
        const roles = this.$store.getters['UserData/getRoles'] || []
        const isTester = roles.includes('tester')
        const isFeatureActive = getFeatures && getFeatures.includes('feedback')
        
        // Show if feature is active (all users) OR if user is tester (even when deactivated)
        return isFeatureActive || isTester
      },
      supportChatEnabled() {
        const getFeatures = this.$store.getters['AppData/getFeatures'] || []
        return Array.isArray(getFeatures) && getFeatures.includes('support-chat')
      },
      supportChatProvider() {
        const supportConfig =
          this.$store.getters['AppData/getSupportChatConfig'] || {}
        const raw =
          typeof supportConfig.provider === 'string'
            ? supportConfig.provider
            : 'lhc'
        const normalized = ['none', 'lhc', 'zendesk', 'lifeOnLifeSupport'].includes(raw)
          ? raw
          : 'lhc'
        return this.supportChatEnabled ? normalized : 'none'
      },
      supportChatConfig() {
        return this.$store.getters['AppData/getSupportChatConfig'] || {}
      },
      notesDrawerOpen() {
        // Disable feedback tab when ANY unit drawer is open (notes, highlights, or comments)
        const notesOpen = this.$store.getters['AppData/notesOpen'] || false
        const highlightsOpen = this.$store.getters['AppData/highlightsOpen'] || false
        const commentsOpen = this.$store.getters['AppData/commentsOpen'] || false
        return notesOpen || highlightsOpen || commentsOpen
      },
      feedbackDrawerOpen: {
        get() {
          return this.feedbackDrawerState
        },
        set(value) {
          this.feedbackDrawerState = value
          localStorage.setItem('feedback_drawer_open', value ? 'true' : 'false')
        },
      },
    },
    components: {
      NavBar,
      Footer,
      FeedbackTab,
      FeedbackDrawer,
      SupportChatLauncher,
      LifeOnLifeSupportLauncher,
      EnvironmentBadge,
    },
    data: () => ({
      churches: [],
      user: null,
      admin: null,
      stopUserWatch: null,
      feedbackDrawerState: false, // Internal state for drawer
    }),
    created() {
      // Listen for notes drawer opening event to close feedback drawer
      emitter.on('closeFeedbackDrawer', this.closeFeedbackForNotes)

      // console.log('auth ',this.$store.getters.getUser)

      // let user = this.$store.getters.getUser
      firebase.auth().onAuthStateChanged((user) => {
        // Reading users needs a signed-in user (firestore.rules), so the
        // listener stops when the user signs out or changes.
        if (this.stopUserWatch) this.stopUserWatch()
        this.stopUserWatch = null
        if (user) {
          this.user = user

          // get current user
          let ref = db.collection('users').where('email', '==', this.user.email)
          this.stopUserWatch = ref.onSnapshot((snapshot) => {
            snapshot.docChanges().forEach((change) => {
              let docs = change.doc
              ;(this.admin = docs.data()), (this.admin.id = docs.data().id)

              // check to make sure the user is an admin
              if (
                this.admin.roles.includes('superAdmin') ||
                this.admin.roles.includes('helpDesk')
              ) {
                // set all churches in the store
                this.$store.dispatch('AppData/allChurches', this.churches)
              } else if (this.admin.cid) {
                // Members read only their own church (firestore.rules)
                db.collection('churches')
                  .where('cid', '==', this.admin.cid)
                  .get()
                  .then((snapshot) => {
                    snapshot.forEach((doc) => {
                      let church = doc.data()
                      this.churches.push(church)
                    })
                  })
                  .catch((err) => console.warn('Could not load church:', err))
              }
            })
          }, (err) => {
            if (firebase.auth().currentUser) console.error('User listener failed:', err)
            this.stopUserWatch = null
          })
        } else {
          this.user = null
        }
      })
    },
    mounted() {
      const saved = localStorage.getItem('dark_theme')
      currentTheme.value = saved === 'true' ? 'dark' : 'light' // ✅ use the ref

      // Keep chat provider behavior in sync with runtime support settings.
      this.$watch(
        () => [
          this.supportChatProvider,
          this.supportChatConfig?.zendesk?.key || '',
          this.supportChatConfig?.lifeOnLifeSupport?.ticketUrl || '',
          this.routeBaseName,
        ],
        () => {
          this.syncSupportChatProvider()
        },
        { immediate: true }
      )

      // Initialize feedback drawer state from localStorage
      // BUT: If we're on a Unit page, don't restore feedback drawer state
      // (it should be closed when notes/highlights/comments drawers are available)
      const isUnitPage = this.$route.name === 'Unit'
      if (!isUnitPage) {
        const savedDrawerState = localStorage.getItem('feedback_drawer_open')
        this.feedbackDrawerState = savedDrawerState === 'true'
      } else {
        // Force close feedback drawer on unit pages
        this.feedbackDrawerState = false
        localStorage.setItem('feedback_drawer_open', 'false')
      }

      // Handle feedback drawer state on route change
      this.$router.afterEach((to) => {
        // When navigating to Unit page, ensure feedback drawer is closed
        if (to.name === 'Unit') {
          this.feedbackDrawerState = false
          localStorage.setItem('feedback_drawer_open', 'false')
        }
      })

      window.addEventListener('beforeprint', this.onBeforePrint)
      window.addEventListener('afterprint', this.onAfterPrint)
    },
    beforeUnmount() {
      // Clean up event listener
      emitter.off('closeFeedbackDrawer', this.closeFeedbackForNotes)
      window.removeEventListener('beforeprint', this.onBeforePrint)
      window.removeEventListener('afterprint', this.onAfterPrint)
      stopLiveHelperChatSuppression()
    },
    methods: {
      async syncSupportChatProvider() {
        const provider = this.supportChatProvider
        document.body.setAttribute('data-support-chat-provider', provider)
        // Never show support launcher on maintenance or print/PDF routes.
        if (this.hideFloatingSupportChrome) {
          document.body.setAttribute('data-support-chat-provider', 'none')
          startLiveHelperChatSuppression()
          hideLiveHelperChat()
          hideZendesk()
          return
        }
        if (provider === 'zendesk') {
          startLiveHelperChatSuppression()
          hideLiveHelperChat()
          await initZendesk(this.supportChatConfig?.zendesk || {})
          showZendesk()
          return
        }
        if (provider === 'lhc') {
          stopLiveHelperChatSuppression()
          hideZendesk()
          initLiveHelperChat(this.supportChatConfig?.lhc || {})
          showLiveHelperChat()
          return
        }
        if (provider === 'lifeOnLifeSupport') {
          startLiveHelperChatSuppression()
          hideLiveHelperChat()
          hideZendesk()
          return
        }
        startLiveHelperChatSuppression()
        hideLiveHelperChat()
        hideZendesk()
      },
      updateBodyClass(baseYear) {
        document.body.className = baseYear
      },
      toggleFeedbackDrawer() {
        // Prevent opening feedback drawer if ANY unit drawer is open
        const notesOpen = this.$store.getters['AppData/notesOpen'] || false
        const highlightsOpen = this.$store.getters['AppData/highlightsOpen'] || false
        const commentsOpen = this.$store.getters['AppData/commentsOpen'] || false
        if (notesOpen || highlightsOpen || commentsOpen) {
          // Don't open feedback drawer if any unit drawer is open
          return
        }
        
        const wasOpen = this.feedbackDrawerOpen
        this.feedbackDrawerOpen = !wasOpen
        
        // If opening feedback drawer (not closing), close notes drawer
        if (!wasOpen) {
          emitter.emit('closeNotesDrawer')
        }
      },
      closeFeedbackForNotes() {
        // Close feedback drawer when unit drawer opens
        this.feedbackDrawerOpen = false
      },
      onBeforePrint() {
        document.body.classList.add('is-printing')
        hideZendesk()
      },
      onAfterPrint() {
        document.body.classList.remove('is-printing')
        this.syncSupportChatProvider()
      },
    },
  }
</script>

<style>
  .lol {
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  .lol svg .a {
    fill: #a0c548;
  }
  .the-journey-logo .a {
    fill: #a5d867;
  }
  .beta {
    color: #a5d867;
  }
  .the-journey-logo .b {
    fill: #383935;
  }
  .green-btn {
    background-color: #a5d867;
  }

  /* Hard guard: when provider is not LHC, keep all known LHC artifacts hidden. */
  body[data-support-chat-provider='none'] #lhc_status_container_page,
  body[data-support-chat-provider='zendesk'] #lhc_status_container_page,
  body[data-support-chat-provider='lifeOnLifeSupport'] #lhc_status_container_page,
  body[data-support-chat-provider='none'] iframe[src*='support.lifeonlife.org/lhc'],
  body[data-support-chat-provider='zendesk'] iframe[src*='support.lifeonlife.org/lhc'],
  body[data-support-chat-provider='lifeOnLifeSupport'] iframe[src*='support.lifeonlife.org/lhc'],
  body[data-support-chat-provider='none'] iframe[src*='/lhc/'],
  body[data-support-chat-provider='zendesk'] iframe[src*='/lhc/'],
  body[data-support-chat-provider='lifeOnLifeSupport'] iframe[src*='/lhc/'],
  body[data-support-chat-provider='none'] [class*='status-widgetv2'],
  body[data-support-chat-provider='zendesk'] [class*='status-widgetv2'],
  body[data-support-chat-provider='lifeOnLifeSupport'] [class*='status-widgetv2'],
  body[data-support-chat-provider='none'] [class*='status_widget_v2'],
  body[data-support-chat-provider='zendesk'] [class*='status_widget_v2'],
  body[data-support-chat-provider='lifeOnLifeSupport'] [class*='status_widget_v2'],
  body[data-support-chat-provider='none'] [class*='status-widget'],
  body[data-support-chat-provider='zendesk'] [class*='status-widget'],
  body[data-support-chat-provider='lifeOnLifeSupport'] [class*='status-widget'],
  body[data-support-chat-provider='none'] [class*='status_widget'],
  body[data-support-chat-provider='zendesk'] [class*='status_widget'],
  body[data-support-chat-provider='lifeOnLifeSupport'] [class*='status_widget'],
  body[data-support-chat-provider='none'] [id*='status-widget'],
  body[data-support-chat-provider='zendesk'] [id*='status-widget'],
  body[data-support-chat-provider='lifeOnLifeSupport'] [id*='status-widget'],
  body[data-support-chat-provider='none'] [id*='status_widget'],
  body[data-support-chat-provider='zendesk'] [id*='status_widget'],
  body[data-support-chat-provider='lifeOnLifeSupport'] [id*='status_widget'],
  body[data-support-chat-provider='none'] .start-chat,
  body[data-support-chat-provider='zendesk'] .start-chat,
  body[data-support-chat-provider='lifeOnLifeSupport'] .start-chat,
  body[data-support-chat-provider='none'] .start-chat-mobile,
  body[data-support-chat-provider='zendesk'] .start-chat-mobile,
  body[data-support-chat-provider='lifeOnLifeSupport'] .start-chat-mobile,
  body[data-support-chat-provider='none'] .start-chat-desktop,
  body[data-support-chat-provider='zendesk'] .start-chat-desktop,
  body[data-support-chat-provider='lifeOnLifeSupport'] .start-chat-desktop {
    display: none !important;
    visibility: hidden !important;
    opacity: 0 !important;
    pointer-events: none !important;
  }

  /* Hide feedback + help-desk chrome from print/PDF output. */
  @media print {
    .feedback-tab,
    .feedback-drawer,
    .support-chat-launcher,
    .lifesupport-launcher,
    #lhc_status_container_page,
    iframe[src*='support.lifeonlife.org/lhc'],
    iframe[src*='/lhc/'],
    [class*='status-widgetv2'],
    [class*='status_widget_v2'],
    [class*='status-widget'],
    [class*='status_widget'],
    [id*='status-widget'],
    [id*='status_widget'],
    .start-chat,
    .start-chat-mobile,
    .start-chat-desktop,
    iframe#launcher,
    iframe#webWidget,
    iframe[id^='webWidget'],
    iframe[title*='Messaging'],
    iframe[name='Messaging window'],
    #launcher,
    .zEWidget-launcher,
    [data-product='web_widget'] {
      display: none !important;
      visibility: hidden !important;
      opacity: 0 !important;
    }
  }

  body.is-printing .feedback-tab,
  body.is-printing .feedback-drawer,
  body.is-printing .support-chat-launcher,
  body.is-printing .lifesupport-launcher,
  body.is-printing #lhc_status_container_page,
  body.is-printing iframe[src*='support.lifeonlife.org/lhc'],
  body.is-printing iframe[src*='/lhc/'],
  body.is-printing iframe#launcher,
  body.is-printing iframe#webWidget,
  body.is-printing iframe[id^='webWidget'],
  body.is-printing #launcher,
  body.is-printing .zEWidget-launcher {
    display: none !important;
    visibility: hidden !important;
    opacity: 0 !important;
  }

  .v-theme--light.v-card > .v-card__text,
  .v-theme--light.v-card .v-card__subtitle {
    color: #000;
  }

  .expansion-panel-header-container {
    display: flex;
    align-items: center;
  }
  .expansion-panel-header-container .handle {
    max-width: 20px;
    margin-left: 20px;
    cursor: grab;
  }
  .expansion-panel-header-container .handle:active {
    cursor: grabbing;
  }
  .expansion-panel-header-container .action-icons {
    display: flex;
    flex-direction: row;
    cursor: pointer;
  }
  .expansion-panel-header-container .action-icons * {
    margin-right: 20px;
  }
  .expansion-panel-header-container .panel-header {
    line-height: 1;
    min-height: 48px;
    padding: 16px 24px;
    width: 100%;
  }

  .v-expansion-panel {
    border: 1px solid #b5b5b5;
    border-radius: 4px;
  }
  .v-expansion-panel::before {
    box-shadow: none !important;
  }
  .v-expansion-panel::after {
    border-top: none !important;
  }
  .tj-elevation-1 {
    box-shadow: 0px 2px 6px rgba(0, 0, 0, 0.04);
  }

  .tj-delete-modal .dg-content-cont,
  .swal2-container {
    font-family: 'museo-sans', sans-serif;
  }
  .editor-container {
    padding: 24px;
  }
  .v-list-item__icon:focus,
  .v-list-item__icon svg:focus {
    outline: none;
  }

  /* --- Data tables (kept) --- */
  body
    .v-theme--light.v-data-table
    > .v-table__wrapper
    > table
    > tbody
    > tr:hover:not(.v-data-table__expanded__content):not(
      .v-data-table__empty-wrapper
    ) {
    background: #f3f9e9;
  }
  body
    .v-theme--light.v-data-table
    > .v-table__wrapper
    > table
    > tbody
    > tr.base {
    background: #f9fdf1;
  }
  body
    .v-theme--light.v-data-table
    > .v-table__wrapper
    > table
    > tbody
    > tr.disabled:hover:not(.v-data-table__expanded__content):not(
      .v-data-table__empty-wrapper
    ),
  body
    .v-theme--dark.v-data-table
    > .v-table__wrapper
    > table
    > tbody
    > tr.disabled:hover:not(.v-data-table__expanded__content):not(
      .v-data-table__empty-wrapper
    ) {
    background: transparent;
  }
  body
    .v-data-table
    > .v-table__wrapper
    > table
    > thead
    > tr.v-data-table__progress
    > th {
    padding: 0;
  }
  body
    .v-theme--light.v-data-table
    > .v-table__wrapper
    > table
    > thead
    > tr
    > th,
  body
    .v-theme--dark.v-data-table
    > .v-table__wrapper
    > table
    > thead
    > tr
    > th {
    color: #a5d867 !important;
    text-transform: uppercase;
    font-size: 15px;
    font-weight: 700;
  }
  body .sortable .v-icon {
    width: 10px !important;
    height: 10px !important;
    margin-left: 5px;
  }
  body .v-data-table__expand-icon--active {
    transform: none !important;
  }
  body .v-data-table__expand-icon--active path {
    fill: #cecece !important;
  }
  body .v-data-table__expand-icon--active path:first-child {
    fill: #a5d867 !important;
  }
  body .v-theme--light .v-data-table tbody tr.v-data-table__expanded__content {
    background: #f8fbf3;
  }
  body .v-data-table tbody tr.v-data-table__expanded__content {
    box-shadow: none;
  }
  body .v-application--is-ltr .v-data-table--fixed-header .v-data-footer {
    margin-right: 0;
    padding-right: 17px;
  }

  .v-text-field {
    padding-bottom: 5px;
  }
  .v-theme--light.v-text-field--outlined:not(.v-input--is-focused):not(
      .v-input--has-state
    )
    > .v-input__control
    > .v-input__slot
    fieldset {
    color: #e8e9ec;
  }

  .v-divider.dashed {
    border: none;
    max-height: inherit;
    background-size: 7px 2px;
    background-repeat: repeat-x;
    height: 2px;
    opacity: 1;
  }
  .v-theme--light.v-divider.dashed {
    background-image: linear-gradient(
      to right,
      #acacac 33%,
      rgba(255, 255, 255, 0) 0%
    );
  }
  .v-theme--dark.v-divider.dashed {
    background-image: linear-gradient(
      to right,
      rgba(255, 255, 255, 0.42) 33%,
      rgba(255, 255, 255, 0) 0%
    );
  }

  .v-theme--light:not(.v-label--is-disabled).v-label {
    color: #000000;
  }
  .v-theme--dark.v-application .ql-formats {
    color: #3e3e3e;
  }

  .v-expansion-panel .v-label {
    font-weight: 600;
    text-transform: uppercase;
  }

  .v-theme--light .disabled {
    color: rgba(0, 0, 0, 0.38);
  }
  .v-theme--dark .disabled {
    color: rgba(255, 255, 255, 0.38);
  }
  .text-primary.v-theme--light.v-list-item--disabled {
    color: rgba(0, 0, 0, 0.38) !important;
  }

  .close-link {
    cursor: pointer;
    font-size: 15px;
    display: flex;
    flex-direction: row;
    align-items: center;
  }
  .close-link-text {
    margin-left: 7px;
    font-weight: 600;
  }

  .sub-panel > .v-expansion-panel-content__wrap {
    padding-left: 0;
    padding-right: 0;
  }

  .v-theme--light .black-circle-checkboxes .v-icon {
    color: #000;
  }
  .v-theme--dark .black-circle-checkboxes .v-icon {
    color: rgba(255, 255, 255, 0.3);
  }
  .v-checkbox-btn .v-label {
    text-transform: uppercase;
    font-size: 13px;
  }

  .quillWrapper.busy {
    box-shadow: 0 0 12px rgba(165, 216, 103, 0.45);
  }

  .snippet-autosave-header {
    padding: 10px;
  }
  .snippet-autosave-status {
    display: flex;
    align-items: center;
  }
  .snippet-autosave-status_label {
    font-weight: bold;
    margin-right: 10px;
  }
  .snippet-autosave-status_spinner {
    display: flex;
    align-items: center;
    position: relative;
  }
  .snippet-autosave-status_spinner-label {
    position: relative;
  }
  .snippet-autosave-status.busy .snippet-autosave-status_spinner-label::after {
    content: 'Saving...';
    color: red;
  }
  .snippet-autosave-status_spinner-label::after {
    content: 'Ready!';
    color: #01ba01;
    display: inline-block;
    margin-right: 10px;
  }
  .snippet-autosave-status.busy .snippet-autosave-status_spinner-loader {
    display: block;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    border-top: 3px solid hsl(0, 0%, 70%);
    border-right: 2px solid transparent;
    animation: autosave-status-spinner 1s linear infinite;
  }

  #image-url-overlay {
    z-index: 6;
  }
  #imageUrlDialog {
    align-items: center;
    display: none;
    height: 100%;
    justify-content: center;
    left: 0;
    position: fixed;
    top: 0;
    transition: 0.2s cubic-bezier(0.25, 0.8, 0.25, 1), z-index 1ms;
    width: 100%;
    z-index: 10;
    outline: none;
  }

  .v-list-item__icon svg.lock-icon {
    margin-left: auto;
  }

  /* Removed: .base-year / .current-year / .current-course-* / .base-year-icon /
     year color variants / list-group prepend/overlay rules — now scoped in CurrentCourseMenu.vue */

  .enable-pointer {
    pointer-events: all;
    cursor: default;
  }

  span.comment {
    text-decoration: underline;
    font-weight: 600;
  }

  .allow-commenting {
    position: relative;
  }

  body .v-tour .v-step__button {
    font-size: 0.9rem;
    height: 1.9rem;
    padding: 0.35rem 0.7rem;
  }
  body .v-tour .v-step {
    background: #555650;
  }
  body .v-tour .v-step__header {
    background-color: #383935;
  }
  body .v-tour .v-step .v-step__arrow--dark {
    border-color: #383935;
  }
  body .v-tour .v-step .v-step__arrow--dark:before {
    background: inherit;
  }
  body .v-pagination__navigation,
  body .v-pagination__item {
    box-shadow: none;
  }
  body .ql-snow.ql-toolbar button {
    width: auto;
  }

  @keyframes autosave-status-spinner {
    to {
      transform: rotate(360deg);
    }
  }

  .rtTooltipBody .ch-ref {
    font-weight: 400;
  }
  .rtTooltipSocialIcon#rtTwitter {
    background-position: 0 -190px;
  }
  .rtTooltipSocialIcon#rtFacebook {
    background-position: 0 -114px;
  }
  .greenYear .rtLight .rtTooltipHeader {
    background: #024731;
  }
  .blueYear .rtLight .rtTooltipHeader {
    background: #002b45;
  }
  .redYear .rtLight .rtTooltipHeader {
    background: #6e273d;
  }
</style>
